import FingerprintJS from '@fingerprintjs/fingerprintjs'
import UAParser from 'ua-parser-js'

const TIMEOUT_MS = 1800
const TELEMETRY_VERSION = '2.0.0'
const CACHE_KEY = 'bonna_telemetry_cache'
const CACHE_TTL_MS = 30 * 60 * 1000

const FALLBACK_PAYLOAD = {
  deviceId: 'unknown',
  publicIp: 'unknown',
  publicIsp: 'unknown',
  publicCity: 'unknown',
  localIp: 'unknown',
  deviceType: 'unknown',
  deviceBrand: 'unknown',
  osName: 'unknown',
  osVersion: 'unknown',
  browserName: 'unknown',
  browserVersion: 'unknown',
  cores: 'unknown',
  ramGb: 'unknown',
  screenWidth: 0,
  screenHeight: 0,
  devicePixelRatio: 0,
  isTouchEnabled: false,
  screenResolution: { width: 0, height: 0, devicePixelRatio: 0 },
  gpuVendor: 'unknown',
  gpuRenderer: 'unknown',
  canvasFingerprint: 'unknown',
  batteryLevel: 'unknown',
  batteryCharging: 'unknown',
  connectionType: 'unknown',
  timezone: 'unknown',
  userAgent: 'unknown',
  language: 'unknown',
  timestamp: new Date().toISOString(),
  telemetryVersion: TELEMETRY_VERSION,
}

let memoryCache = null

const withTimeout = (promise, ms, fallback) =>
  Promise.race([
    promise,
    new Promise((resolve) => setTimeout(() => resolve(fallback), ms)),
  ])

const safeSync = (fn, fallback) => {
  try {
    return fn()
  } catch {
    return fallback
  }
}

const safeAsync = async (fn, fallback) => {
  try {
    return await fn()
  } catch {
    return fallback
  }
}

const sha256Hex = async (input) => {
  if (!window.crypto?.subtle) return null
  const data = new TextEncoder().encode(input)
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', data)
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

const isPrivateIPv4 = (ip) => {
  if (ip.startsWith('10.') || ip.startsWith('192.168.')) return true
  if (!ip.startsWith('172.')) return false
  const secondOctet = parseInt(ip.split('.')[1], 10)
  return secondOctet >= 16 && secondOctet <= 31
}

const readCache = () => {
  if (memoryCache && Date.now() - memoryCache.cachedAt < CACHE_TTL_MS) {
    return { ...memoryCache.payload, timestamp: new Date().toISOString() }
  }

  try {
    const raw = sessionStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed?.cachedAt || Date.now() - parsed.cachedAt >= CACHE_TTL_MS) {
      sessionStorage.removeItem(CACHE_KEY)
      return null
    }
    memoryCache = parsed
    return { ...parsed.payload, timestamp: new Date().toISOString() }
  } catch {
    return null
  }
}

const writeCache = (payload) => {
  const entry = { payload, cachedAt: Date.now() }
  memoryCache = entry
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(entry))
  } catch {
    // sessionStorage full or blocked
  }
}

const detectDeviceType = (uaResult) => {
  const type = uaResult.device.type

  if (type === 'mobile') {
    if (uaResult.os.name === 'iOS') return 'iPhone'
    if (uaResult.os.name === 'Android') return 'Android Phone'
    return 'Mobile'
  }

  if (type === 'tablet') {
    if (uaResult.os.name === 'iOS') return 'iPad'
    if (uaResult.os.name === 'Android') return 'Android Tablet'
    return 'Tablet'
  }

  if (uaResult.os.name === 'Windows') return 'Windows Desktop'
  if (uaResult.os.name === 'Mac OS') return 'MacBook / Mac'
  if (uaResult.os.name === 'Linux') return 'Linux Desktop'

  return 'Desktop'
}

const collectCoreFingerprint = () =>
  safeAsync(async () => {
    const fp = await FingerprintJS.load()
    const result = await fp.get()
    return { deviceId: result.visitorId || 'unknown' }
  }, { deviceId: 'unknown' })

const collectPublicNetwork = () =>
  withTimeout(
    safeAsync(async () => {
      let publicIp = 'unknown'
      let publicIsp = 'unknown'
      let publicCity = 'unknown'

      try {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS)
        const res = await fetch('https://ipapi.co/json/', { signal: controller.signal })
        clearTimeout(timeoutId)

        if (res.ok) {
          const data = await res.json()
          publicIp = data.ip ?? 'unknown'
          publicIsp = data.org ?? 'unknown'
          publicCity = data.city ?? 'unknown'
        }
      } catch {
        // ipapi blocked or timed out — fall through to ipify
      }

      if (publicIp === 'unknown') {
        try {
          const controller = new AbortController()
          const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS)
          const res = await fetch('https://api.ipify.org?format=json', { signal: controller.signal })
          clearTimeout(timeoutId)
          if (res.ok) {
            const data = await res.json()
            publicIp = data.ip ?? 'unknown'
          }
        } catch {
          publicIp = 'blocked'
        }
      }

      return { publicIp, publicIsp, publicCity }
    }, { publicIp: 'blocked', publicIsp: 'blocked', publicCity: 'blocked' }),
    TIMEOUT_MS,
    { publicIp: 'unknown', publicIsp: 'unknown', publicCity: 'unknown' }
  )

const collectLocalNetwork = () =>
  withTimeout(
    new Promise((resolve) => {
      try {
        const RTCPeerConnection =
          window.RTCPeerConnection ||
          window.webkitRTCPeerConnection ||
          window.mozRTCPeerConnection

        if (!RTCPeerConnection) {
          resolve({ localIp: 'blocked' })
          return
        }

        const pc = new RTCPeerConnection({
          iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
        })

        const ipAddresses = new Set()
        let settled = false

        const finish = (value) => {
          if (settled) return
          settled = true
          try {
            pc.close()
          } catch {
            // already closed
          }
          resolve({ localIp: value })
        }

        pc.createDataChannel('telemetry')

        pc.createOffer()
          .then((offer) => pc.setLocalDescription(offer))
          .catch(() => finish('blocked'))

        pc.onicecandidate = (event) => {
          if (!event?.candidate) {
            finish(ipAddresses.size > 0 ? Array.from(ipAddresses).join(', ') : 'unknown')
            return
          }

          const candidate = event.candidate.candidate
          if (candidate.includes('.local')) return

          const match = /(\d{1,3}(?:\.\d{1,3}){3})/.exec(candidate)
          if (match && isPrivateIPv4(match[1])) {
            ipAddresses.add(match[1])
          }
        }

        setTimeout(() => {
          finish(ipAddresses.size > 0 ? Array.from(ipAddresses).join(', ') : 'unknown')
        }, TIMEOUT_MS)
      } catch {
        resolve({ localIp: 'blocked' })
      }
    }),
    TIMEOUT_MS,
    { localIp: 'unknown' }
  )

const collectHardwareSpecs = () =>
  safeSync(() => {
    const screenWidth = window.screen?.width ?? 0
    const screenHeight = window.screen?.height ?? 0
    const devicePixelRatio = window.devicePixelRatio ?? 0

    return {
      cores: navigator.hardwareConcurrency ?? 'unknown',
      ramGb: navigator.deviceMemory ?? 'unknown',
      screenWidth,
      screenHeight,
      devicePixelRatio,
      isTouchEnabled:
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        navigator.msMaxTouchPoints > 0,
    }
  }, {
    cores: 'unknown',
    ramGb: 'unknown',
    screenWidth: 0,
    screenHeight: 0,
    devicePixelRatio: 0,
    isTouchEnabled: false,
  })

const collectGpuFingerprint = () =>
  safeSync(() => {
    const canvas = document.createElement('canvas')
    const gl =
      canvas.getContext('webgl') || canvas.getContext('experimental-webgl')

    if (!gl) {
      return { gpuVendor: 'blocked', gpuRenderer: 'blocked' }
    }

    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info')
    if (!debugInfo) {
      return { gpuVendor: 'unknown', gpuRenderer: 'unknown' }
    }

    return {
      gpuVendor: gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || 'unknown',
      gpuRenderer: gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || 'unknown',
    }
  }, { gpuVendor: 'blocked', gpuRenderer: 'blocked' })

const collectCanvasFingerprint = () =>
  safeAsync(async () => {
    const canvas = document.createElement('canvas')
    canvas.width = 280
    canvas.height = 60

    const ctx = canvas.getContext('2d')
    if (!ctx) return { canvasFingerprint: 'blocked' }

    ctx.textBaseline = 'top'

    const gradient = ctx.createLinearGradient(0, 0, 280, 0)
    gradient.addColorStop(0, '#f60')
    gradient.addColorStop(1, '#069')
    ctx.fillStyle = gradient
    ctx.fillRect(0, 0, 280, 60)

    ctx.fillStyle = '#fff'
    ctx.fillRect(125, 1, 62, 20)

    ctx.fillStyle = '#069'
    ctx.font = '14px Arial'
    ctx.fillText('Bonna Telemetry', 2, 15)

    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)'
    ctx.font = '18px "Times New Roman"'
    ctx.fillText('🎯 fingerprint', 4, 35)

    ctx.shadowColor = 'rgba(0,0,0,0.3)'
    ctx.shadowBlur = 4
    ctx.strokeStyle = '#c00'
    ctx.beginPath()
    ctx.arc(240, 30, 18, 0, Math.PI * 2)
    ctx.stroke()

    ctx.beginPath()
    ctx.moveTo(180, 10)
    ctx.bezierCurveTo(200, 50, 220, 5, 250, 45)
    ctx.lineWidth = 2
    ctx.strokeStyle = '#333'
    ctx.stroke()

    let dataUrl
    try {
      dataUrl = canvas.toDataURL('image/png')
    } catch {
      return { canvasFingerprint: 'blocked' }
    }

    const hash = await sha256Hex(dataUrl)
    if (hash) return { canvasFingerprint: hash }

    return { canvasFingerprint: dataUrl.slice(0, 64) || 'blocked' }
  }, { canvasFingerprint: 'blocked' })

const collectEnvironmentState = () =>
  safeAsync(async () => {
    const timezone = safeSync(
      () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown',
      'unknown'
    )

    const connection =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection

    const connectionType =
      connection?.effectiveType || connection?.type || 'unknown'

    let batteryLevel = 'unknown'
    let batteryCharging = 'unknown'

    if (navigator.getBattery) {
      const battery = await withTimeout(
        navigator.getBattery(),
        TIMEOUT_MS,
        null
      )

      if (battery) {
        batteryLevel = typeof battery.level === 'number' ? battery.level : 'unknown'
        batteryCharging =
          typeof battery.charging === 'boolean' ? battery.charging : 'unknown'
      }
    }

    return { batteryLevel, batteryCharging, connectionType, timezone }
  }, {
    batteryLevel: 'unknown',
    batteryCharging: 'unknown',
    connectionType: 'unknown',
    timezone: 'unknown',
  })

const collectSoftwareDevice = () =>
  safeSync(() => {
    const parser = new UAParser()
    const uaResult = parser.getResult()

    return {
      deviceType: detectDeviceType(uaResult),
      deviceBrand: uaResult.device.vendor || 'unknown',
      osName: uaResult.os.name || 'unknown',
      osVersion: uaResult.os.version || 'unknown',
      browserName: uaResult.browser.name || 'unknown',
      browserVersion: uaResult.browser.version || 'unknown',
      userAgent: navigator.userAgent || 'unknown',
      language: navigator.language || 'unknown',
    }
  }, {
    deviceType: 'unknown',
    deviceBrand: 'unknown',
    osName: 'unknown',
    osVersion: 'unknown',
    browserName: 'unknown',
    browserVersion: 'unknown',
    userAgent: 'unknown',
    language: 'unknown',
  })

const unwrapSettled = (result, fallback) => {
  if (result.status === 'fulfilled' && result.value && typeof result.value === 'object') {
    return result.value
  }
  return fallback
}

const buildScreenResolution = (partial) => ({
  width: partial.screenWidth ?? 0,
  height: partial.screenHeight ?? 0,
  devicePixelRatio: partial.devicePixelRatio ?? 0,
})

/**
 * Paranoid-level device telemetry for anonymous form submissions.
 * Never throws — every collector fails safe with 'unknown' or 'blocked'.
 */
export const getDeviceInfo = async () => {
  const cached = readCache()
  if (cached) return cached

  try {
    const [
      coreResult,
      publicResult,
      localResult,
      hardwareResult,
      gpuResult,
      canvasResult,
      envResult,
      softwareResult,
    ] = await Promise.allSettled([
      collectCoreFingerprint(),
      collectPublicNetwork(),
      collectLocalNetwork(),
      Promise.resolve(collectHardwareSpecs()),
      Promise.resolve(collectGpuFingerprint()),
      collectCanvasFingerprint(),
      collectEnvironmentState(),
      Promise.resolve(collectSoftwareDevice()),
    ])

    const merged = {
      ...FALLBACK_PAYLOAD,
      ...unwrapSettled(coreResult, { deviceId: 'unknown' }),
      ...unwrapSettled(publicResult, { publicIp: 'unknown', publicIsp: 'unknown', publicCity: 'unknown' }),
      ...unwrapSettled(localResult, { localIp: 'unknown' }),
      ...unwrapSettled(hardwareResult, {}),
      ...unwrapSettled(gpuResult, { gpuVendor: 'unknown', gpuRenderer: 'unknown' }),
      ...unwrapSettled(canvasResult, { canvasFingerprint: 'unknown' }),
      ...unwrapSettled(envResult, {}),
      ...unwrapSettled(softwareResult, {}),
      screenResolution: buildScreenResolution(
        unwrapSettled(hardwareResult, {})
      ),
      timestamp: new Date().toISOString(),
      telemetryVersion: TELEMETRY_VERSION,
    }

    writeCache(merged)
    return merged
  } catch (error) {
    console.error('Telemetry error:', error)
    return {
      ...FALLBACK_PAYLOAD,
      timestamp: new Date().toISOString(),
    }
  }
}
